import { ValidationErrorCategory, ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { buildSystemRowLookup } from '../forecast-utils/RowResetUtils';
import { isXvtReadOnlyRow } from '../forecast-utils/column-generator/helpers/AuthorizationHelper';
import { logger } from 'src/analytics/KatalLogger';
import { UserAccessEntity, UserAccessForCurrentBusinessGroup } from 'src/models/XptUsersModel';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import {
  getUserCostCenters,
  getUserCostCentersFromUserAccessEntitiesList,
  getUserProductLines,
  getUserProductLinesFromUserAccessEntitiesList
} from 'src/features/business-group/access-authorization/redux/AccessAuthorizationUtils';

/**
 * Service for validating user authorization in forecast templates
 */
export class AuthorizationValidationService extends ValidationService {
  /**
   * Validates that the user has authorization to modify the rows.
   * @param modifiedRows The array of modified rows to validate.
   * @param userAlias The alias of the current user.
   * @param isAdminUser Whether the current user is an admin.
   * @returns Promise with validation status
   */
  public async validateUnAuthorizedRows(modifiedRows: any[], userAlias: string, isAdminUser: boolean): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];
    logger.info(`Validating unauthorized rows for ${modifiedRows.length} records`);

    modifiedRows.forEach((row, rowIndex) => {
      const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
      const budgetOwner = row[ForecastGridFixedFields.BudgetOwner.value];
      if (!isAdminUser && budgetOwner !== userAlias) {
        validationErrorDetails.push({
          rowIndex: rowNo,
          field: ForecastGridFixedFields.BudgetOwner.value,
          message: `Budget Owners can only modify their own rows. Expected Budget Owner: ${userAlias}. Found ${budgetOwner} ${this.getLineItemIdMessagePart(
            row,
            ForecastGridFixedFields.XptLineItemId.value
          )}.`
        });
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Unauthorized Rows', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_FAILED,
        ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(
      ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_SUCCESS,
      ForecastValidationMessages.UNAUTHORIZED_ROWS_VALIDATION_DEFAULT
    );
  }

  /**
   * Validates that no modified rows fall in an xVT-owned cost center (7802/7113). Rows in those cost
   * centers are read-only in the HDW forecast grid — the backend rejects edits to them, so surface
   * the violation here instead of at save time.
   *
   * The predicate reads the row's Cost Center directly (`row['Cost Center']`), which the uploaded
   * file carries, so the row itself is sufficient. The loaded-counterpart lookup by Line Item Id is
   * kept as a belt-and-suspenders fallback (in case a file blanks the Cost Center on a locked row).
   *
   * @param modifiedRows The array of modified rows to validate.
   * @param forecastTemplateCompleteData The complete loaded forecast data (fallback by Line Item Id).
   * @returns Promise with validation status
   */
  public async validateXvtSourcedRows(modifiedRows: any[], forecastTemplateCompleteData: any[]): Promise<ValidationStatusEntity> {
    try {
      const validationErrorDetails: ValidationErrorDetail[] = [];
      logger.info(`Validating xVT read-only (cost-center) rows for ${modifiedRows.length} records`);

      const completeDataByLineItemId = new Map<string, any>(
        forecastTemplateCompleteData.map((row) => [`${row[ForecastGridFixedFields.XptLineItemId.value]}`, row])
      );

      modifiedRows.forEach((row, rowIndex) => {
        const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
        const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
        const loadedCounterpart = lineItemId ? completeDataByLineItemId.get(`${lineItemId}`) : undefined;

        if (isXvtReadOnlyRow(row) || isXvtReadOnlyRow(loadedCounterpart)) {
          validationErrorDetails.push({
            rowIndex: rowNo,
            message: `Row ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )} is in an xVT-owned cost center (7802/7113) and is read-only. Make changes in the xVT Planning tab instead.`
          });
        }
      });

      this.logValidationErrors('xVT-sourced Rows', validationErrorDetails);

      if (validationErrorDetails.length > 0) {
        return this.createErrorStatus(
          ForecastValidationMessages.XVT_SOURCED_ROWS_VALIDATION_FAILED,
          ForecastValidationMessages.XVT_SOURCED_ROWS_VALIDATION_DEFAULT,
          validationErrorDetails
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.XVT_SOURCED_ROWS_VALIDATION_SUCCESS,
        ForecastValidationMessages.XVT_SOURCED_ROWS_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating xVT-sourced rows:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.XVT_SOURCED_ROWS_VALIDATION_ERROR,
        ForecastValidationMessages.XVT_SOURCED_ROWS_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating xVT-sourced rows' }]
      );
    }
  }

  /**
   * Validates that the cost centers in the file are authorized for the current user.
   * @param forecastTemplateGridData The grid data to validate.
   * @param userAccessForCurrentBusinessGroup The user's access for the current business group.
   * @param forecastTemplateCompleteData Optional system-side row snapshot. When supplied:
   *   - A BASELINE row (`!is_new`) whose Cost Center is UNCHANGED from the system version is
   *     carried over and skipped entirely. Rationale: Cost Center is locked on baseline rows,
   *     so the user can't fix a stale-access hit — the historical assignment stays authoritative
   *     for edits that leave the CC alone. Symmetric with `validateBudgetOwnerAndCostCenterCombination`,
   *     which applies the same carry-over. Without it, a user who only edits forecast months on
   *     an old row could hit a dead-end auth error with no recovery path (Reset would produce
   *     the same still-unauthorized system value, so it wouldn't help either).
   *   - An unauthorized Cost Center on a baseline row whose value was CHANGED from the system
   *     value is tagged with `errorCategory: 'non-editable-field'`. This is the fix for the
   *     case where a user edits the locked Cost Center on a baseline row to a value they don't
   *     own — the pipeline short-circuits here before the non-editable-fields validator can
   *     run, so without this tag the preview grid's "Reset to system values" action would be
   *     disabled, leaving the user with no way out. Reset snaps the row back to the authorized
   *     system value.
   * @returns Promise with validation status
   */
  public async validateCCsInvolvedInExcel(
    forecastTemplateGridData: any[],
    userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup,
    forecastTemplateCompleteData?: any[]
  ): Promise<ValidationStatusEntity> {
    try {
      const currentUserCCs = new Set(getUserCostCenters(userAccessForCurrentBusinessGroup));
      const perRowErrors: ValidationErrorDetail[] = [];
      const invalidCCsSeen = new Set<string>();

      const systemRowByLineItemId = forecastTemplateCompleteData ? buildSystemRowLookup(forecastTemplateCompleteData) : undefined;

      // Walk rows so we can emit per-row + per-field error details — the preview grid uses
      // these to paint the offending Cost Center cells red and lets the user jump directly
      // to the row via the prev/next-error nav.
      forecastTemplateGridData.forEach((row) => {
        const costCenter = row[CorpSegmentNames.COST_CENTER];
        if (costCenter == null || costCenter === '') return;
        const costCenterStr = `${costCenter}`;
        if (currentUserCCs.has(costCenterStr)) return;

        // Baseline-row carry-over: unchanged CC on a `!is_new` row is authoritative even if
        // the current user has since lost access. See method doc.
        const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
        const systemRow = systemRowByLineItemId && lineItemId != null ? systemRowByLineItemId.get(`${lineItemId}`) : undefined;
        if (systemRow && !row[ForecastGridFixedFields.IsNew.value] && `${systemRow[CorpSegmentNames.COST_CENTER]}` === costCenterStr) {
          return;
        }

        invalidCCsSeen.add(costCenterStr);
        const rowNo = row[ForecastGridFixedFields.FileRowNumber.value];

        // Baseline-row edit detection: does the user's Cost Center differ from the system's
        // for this Line Item? If yes, this is fundamentally a non-editable-field violation
        // (Cost Center on baseline rows is locked) and Reset can fix it — tag it so.
        let category: ValidationErrorCategory | undefined;
        if (systemRow && `${systemRow[CorpSegmentNames.COST_CENTER]}` !== costCenterStr) {
          category = 'non-editable-field';
        }

        perRowErrors.push({
          message: `Unauthorized Cost Center "${costCenterStr}" at row ${rowNo}`,
          field: CorpSegmentNames.COST_CENTER,
          rowIndex: typeof rowNo === 'number' ? rowNo : undefined,
          ...(category ? { errorCategory: category } : {})
        });
      });

      if (perRowErrors.length > 0) {
        this.logValidationErrors(
          "Cost Center's",
          Array.from(invalidCCsSeen).map((cc) => ({ message: cc }))
        );
        return this.createErrorStatus(
          ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_FAILED,
          ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_DEFAULT,
          perRowErrors
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_SUCCESS,
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Cost Centers:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_ERROR,
        ForecastValidationMessages.CC_AUTHORIZATION_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating cost centers' }]
      );
    }
  }

  /**
   * Validates that the budget owners in the file are authorized.
   * @param forecastTemplateGridData The grid data to validate.
   * @param userAccessForCurrentBusinessGroup The user's access for the current business group.
   * @param forecastTemplateCompleteData Optional system-side row snapshot. When supplied,
   *   baseline rows (`!is_new`) whose Budget Owner is UNCHANGED from the system version are
   *   carried over and skipped — symmetric with the Cost Center check and with
   *   `validateBudgetOwnerAndCostCenterCombination`. See `validateCCsInvolvedInExcel` for
   *   the full rationale.
   * @returns Promise with validation status
   */
  public async validateBudgetOwnersInvolvedInExcel(
    forecastTemplateGridData: any[],
    userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup,
    forecastTemplateCompleteData?: any[]
  ): Promise<ValidationStatusEntity> {
    try {
      if (!userAccessForCurrentBusinessGroup.isBudgetOwner) {
        return this.createSuccessStatus(
          ForecastValidationMessages.BUDGET_OWNER_VALIDATION_SUCCESS,
          ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT
        );
      } else {
        const currentUser = userAccessForCurrentBusinessGroup.user_alias;
        const perRowErrors: ValidationErrorDetail[] = [];
        const invalidOwnersSeen = new Set<string>();

        // Same pattern as validateCCsInvolvedInExcel — see its doc comment. Baseline rows with
        // a Budget Owner change to an unauthorized alias get tagged as `non-editable-field`
        // so the preview's Reset action can rescue the user (snapping BO back to the system
        // value clears both the auth error and the non-editable-field violation at once).
        const systemRowByLineItemId = forecastTemplateCompleteData ? buildSystemRowLookup(forecastTemplateCompleteData) : undefined;

        // Walk rows so we can emit per-row + per-field error details for the preview grid to
        // paint the offending Budget Owner cells red — same treatment as Cost Center.
        forecastTemplateGridData.forEach((row) => {
          const budgetOwnerInThisRow = row[ForecastGridFixedFields.BudgetOwner.value];
          if (!budgetOwnerInThisRow || budgetOwnerInThisRow === currentUser) return;

          // Baseline-row carry-over: unchanged BO on a `!is_new` row is authoritative even if
          // the row now points to someone other than the current user. See method doc.
          const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
          const systemRow = systemRowByLineItemId && lineItemId != null ? systemRowByLineItemId.get(`${lineItemId}`) : undefined;
          if (
            systemRow &&
            !row[ForecastGridFixedFields.IsNew.value] &&
            `${systemRow[ForecastGridFixedFields.BudgetOwner.value]}` === `${budgetOwnerInThisRow}`
          ) {
            return;
          }

          invalidOwnersSeen.add(budgetOwnerInThisRow);
          const rowNo = row[ForecastGridFixedFields.FileRowNumber.value];

          let category: ValidationErrorCategory | undefined;
          if (systemRow && `${systemRow[ForecastGridFixedFields.BudgetOwner.value]}` !== `${budgetOwnerInThisRow}`) {
            category = 'non-editable-field';
          }

          perRowErrors.push({
            message: `Unauthorized Budget Owner "${budgetOwnerInThisRow}" at row ${rowNo}`,
            field: ForecastGridFixedFields.BudgetOwner.value,
            rowIndex: typeof rowNo === 'number' ? rowNo : undefined,
            ...(category ? { errorCategory: category } : {})
          });
        });

        if (perRowErrors.length > 0) {
          this.logValidationErrors(
            "Budget Owner's",
            Array.from(invalidOwnersSeen).map((bo) => ({ message: bo }))
          );
          return this.createErrorStatus(
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_FAILED,
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT,
            perRowErrors
          );
        } else {
          return this.createSuccessStatus(
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_SUCCESS,
            ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT
          );
        }
      }
    } catch (error: any) {
      logger.error('Error validating Budget Owners:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.BUDGET_OWNER_VALIDATION_ERROR,
        ForecastValidationMessages.BUDGET_OWNER_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating budget owners' }]
      );
    }
  }

  /**
   * Validates that the budget owner and cost center combinations are valid.
   * @param forecastTemplateGridData The grid data to validate.
   * @param xptUsersOfCurrentDataClassification The users of the current data classification.
   * @param userAccessForCurrentBusinessGroup The user's access for the current business group.
   * @param isDMM Whether this is the DMM business group (auth is scoped by Product Line, not Cost Center).
   * @param forecastTemplateCompleteData Full snapshot of the system's stored rows. When supplied,
   *   baseline rows (`!is_new`) whose Budget Owner AND scoped segment (PL for DMM, CC otherwise)
   *   match the system version are skipped — the historical combination is carried over even if
   *   the owner has since lost access. Without this guard, a user editing ONLY forecast month
   *   values on an old row would hit a "combination is invalid" error they cannot fix (both
   *   fields are locked on baseline rows). Optional to keep back-compat with the main-grid
   *   submit path where the check runs on already-loaded rows.
   * @returns Promise with validation status
   */
  public async validateBudgetOwnerAndCostCenterCombination(
    forecastTemplateGridData: any[],
    xptUsersOfCurrentDataClassification: UserAccessEntity[],
    userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup,
    isDMM: boolean,
    forecastTemplateCompleteData?: any[]
  ): Promise<ValidationStatusEntity> {
    try {
      const validationErrorDetails: ValidationErrorDetail[] = [];
      logger.info(`Validating budget owner and cc combination for ${forecastTemplateGridData.length} records`);

      // DMM access is scoped by Product Line; every other business group is scoped by Cost Center.
      const segmentField = isDMM ? CorpSegmentNames.PRODUCT_LINE : CorpSegmentNames.COST_CENTER;
      const segmentLabel = isDMM ? 'Product Line' : 'Cost Center';
      const getCurrentUserSegments = isDMM ? getUserProductLines : getUserCostCenters;
      const getOtherUserSegments = isDMM ? getUserProductLinesFromUserAccessEntitiesList : getUserCostCentersFromUserAccessEntitiesList;

      const currentUserSegments = getCurrentUserSegments(userAccessForCurrentBusinessGroup);

      // XptLineItemId -> systemRow lookup so we can compare the row's Budget Owner + segment to
      // what the system has stored. Only built when the caller supplied completeData; the
      // main-grid submit path uses the runtime-loaded values directly and doesn't need it.
      const systemRowByLineItemId = forecastTemplateCompleteData ? buildSystemRowLookup(forecastTemplateCompleteData) : undefined;

      forecastTemplateGridData.forEach((row, rowIndex) => {
        const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
        const budgetOwner = `${row[ForecastGridFixedFields.BudgetOwner.value]}`;
        const segmentValue = `${row[segmentField]}`;

        // Carry over baseline rows whose auth-relevant fields are unchanged. If a user only
        // edited forecast month values on a row that was submitted long ago — before its Budget
        // Owner lost access — the historical (BudgetOwner, segment) pair is still authoritative.
        // Re-authorizing here would surface an error the user can't fix on their own (both
        // fields are locked on baseline rows), which is the exact trap this branch avoids.
        if (systemRowByLineItemId && !row[ForecastGridFixedFields.IsNew.value]) {
          const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
          const systemRow = lineItemId != null ? systemRowByLineItemId.get(`${lineItemId}`) : undefined;
          if (systemRow) {
            const boUnchanged = `${systemRow[ForecastGridFixedFields.BudgetOwner.value]}` === budgetOwner;
            const segUnchanged = `${systemRow[segmentField]}` === segmentValue;
            if (boUnchanged && segUnchanged) return;
          }
        }

        // If Budget Owner field is current user, check against current user's segments.
        if (budgetOwner === userAccessForCurrentBusinessGroup.user_alias) {
          if (!currentUserSegments.includes(segmentValue)) {
            validationErrorDetails.push({
              rowIndex: rowNo,
              field: segmentField,
              message: `Budget Owner ${budgetOwner} doesn't have access to ${segmentLabel} ${segmentValue}.`
            });
          }
        } else {
          if (budgetOwner && segmentValue) {
            const otherUserSegments = getOtherUserSegments(budgetOwner, xptUsersOfCurrentDataClassification);
            if (!otherUserSegments.includes(segmentValue)) {
              validationErrorDetails.push({
                rowIndex: rowNo,
                field: segmentField,
                message: `Budget Owner ${budgetOwner} doesn't have access to ${segmentLabel} ${segmentValue}.`
              });
            }
          }
        }
      });

      // Log validation errors if any
      this.logValidationErrors('Budget Owner & Cost Center Combination', validationErrorDetails);

      // Return appropriate validation status
      if (validationErrorDetails.length > 0) {
        return this.createErrorStatus(
          isDMM
            ? ForecastValidationMessages.DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_FAILED
            : ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_FAILED,
          isDMM
            ? ForecastValidationMessages.DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT
            : ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT,
          validationErrorDetails
        );
      }

      return this.createSuccessStatus(
        isDMM
          ? ForecastValidationMessages.DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_SUCCESS
          : ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_SUCCESS,
        isDMM
          ? ForecastValidationMessages.DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT
          : ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Budget Owner & Cost Center Mapping:', error);
      return this.createErrorStatus(
        isDMM
          ? ForecastValidationMessages.DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_ERROR
          : ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_ERROR,
        isDMM
          ? ForecastValidationMessages.DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT
          : ForecastValidationMessages.BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating budget owner and cost center combination' }]
      );
    }
  }
}

// Export singleton instance
export const authorizationValidationService = new AuthorizationValidationService();
